import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Plus, Eye, EyeOff, Trash2, LogOut, Music, CalendarPlus, FileText, Bell, Upload, Image as ImageIcon, Users } from "lucide-react";
import { Logo } from "../components/site/Logo";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("abc_admin_token")}` });

const COURSE_LABELS = { songs: "4 Songs 4 Weeks", indie: "Ultimate Indie Musician" };

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_ABBR = { Monday: "Mon", Tuesday: "Tue", Wednesday: "Wed", Thursday: "Thu", Friday: "Fri", Saturday: "Sat", Sunday: "Sun" };
const DAY_INDEX = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };

const firstClassDate = (ym, days) => {
  const [y, m] = ym.split("-").map(Number);
  const dayNums = new Set((days && days.length ? days : ["Wednesday", "Saturday"]).map((d) => DAY_INDEX[d]));
  const d = new Date(y, m - 1, 1);
  while (!dayNums.has(d.getDay())) d.setDate(d.getDate() + 1);
  return `${y}-${String(m).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const batchName = (ym, days) => {
  const [y, m] = ym.split("-").map(Number);
  const month = MONTH_NAMES[m - 1];
  const sorted = (days && days.length ? [...days] : ["Wednesday", "Saturday"]).sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
  const daySlug = sorted.map((d) => DAY_ABBR[d]).join("/");
  const yearSuffix = y === 2026 ? "" : ` '${String(y).slice(2)}`;
  return `${month}${yearSuffix} ${daySlug} Sessions`;
};

const DaysPicker = ({ courseId, value, onChange }) => {
  const toggle = (day) => {
    const has = value.includes(day);
    if (has) return onChange(value.filter((d) => d !== day));
    if (value.length >= 2) return onChange([value[1], day]); // slide: drop oldest, keep newest
    onChange([...value, day]);
  };
  return (
    <div className="flex gap-1" data-testid={`admin-days-picker-${courseId}`}>
      {DAY_ORDER.map((d) => {
        const sel = value.includes(d);
        return (
          <button
            key={d}
            type="button"
            data-testid={`admin-day-${d.toLowerCase()}-${courseId}`}
            onClick={() => toggle(d)}
            className={`font-mono text-[10px] tracking-[0.1em] uppercase w-11 py-2 border transition-colors ${
              sel ? "bg-[#DB0909] border-[#DB0909] text-[#f7f6f2]" : "border-[rgba(28,22,16,0.2)] text-[#4a3e30] hover:border-[#DB0909] hover:text-[#DB0909]"
            }`}
          >
            {DAY_ABBR[d].slice(0, 2)}
          </button>
        );
      })}
    </div>
  );
};

const MonthPicker = ({ courseId, value, onChange }) => {
  const [year, setYear] = useState(value ? Number(value.split("-")[0]) : new Date().getFullYear());
  const selYear = value ? Number(value.split("-")[0]) : null;
  const selMonth = value ? Number(value.split("-")[1]) : null;
  return (
    <div className="w-[240px]">
      <div className="flex items-center justify-between border border-[rgba(28,22,16,0.3)] px-1 py-1">
        <button data-testid={`admin-year-prev-${courseId}`} onClick={() => setYear(year - 1)} className="w-9 h-9 text-lg text-[#4a3e30] hover:text-[#DB0909] hover:bg-[#DB0909]/10 transition-colors">‹</button>
        <span data-testid={`admin-year-display-${courseId}`} className="font-display font-black text-lg text-[#1c1610]">{year}</span>
        <button data-testid={`admin-year-next-${courseId}`} onClick={() => setYear(year + 1)} className="w-9 h-9 text-lg text-[#4a3e30] hover:text-[#DB0909] hover:bg-[#DB0909]/10 transition-colors">›</button>
      </div>
      <div className="mt-1.5 grid grid-cols-4 gap-1">
        {MONTH_NAMES.map((m, i) => {
          const sel = selYear === year && selMonth === i + 1;
          return (
            <button
              key={m}
              data-testid={`admin-month-${i + 1}-${courseId}`}
              onClick={() => onChange(`${year}-${String(i + 1).padStart(2, "0")}`)}
              className={`font-mono text-[11px] py-2 border transition-colors ${
                sel ? "bg-[#DB0909] border-[#DB0909] text-[#f7f6f2]" : "border-[rgba(28,22,16,0.18)] text-[#4a3e30] hover:border-[#DB0909] hover:text-[#DB0909]"
              }`}
            >
              {m.slice(0, 3).toUpperCase()}
            </button>
          );
        })}
      </div>
    </div>
  );
};


const StrengthEditor = ({ b, onSave }) => {
  const [v, setV] = useState(b.seats_total);
  useEffect(() => setV(b.seats_total), [b.seats_total]);
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={Math.max(1, b.seats_taken)}
        value={v}
        onChange={(e) => setV(e.target.value)}
        data-testid={`admin-strength-input-${b.id}`}
        className="w-16 border border-[rgba(28,22,16,0.3)] px-2 py-1 font-mono font-bold text-sm bg-transparent focus:outline-none focus:border-[#DB0909]"
      />
      {Number(v) !== b.seats_total && Number(v) >= Math.max(1, b.seats_taken) && (
        <button data-testid={`admin-strength-save-${b.id}`} onClick={() => onSave(Number(v))} className="font-mono text-[10px] tracking-[0.15em] uppercase bg-[#1c1610] text-[#f7f6f2] px-3 py-1.5 hover:bg-[#DB0909] transition-colors">
          Save
        </button>
      )}
    </div>
  );
};

const AddBatchForm = ({ courseId, presetYm, onCreated, onCancel, compact }) => {
  const [open, setOpen] = useState(!!presetYm);
  const [ym, setYm] = useState(presetYm || "");
  const [seats, setSeats] = useState(10);
  const [days, setDays] = useState(["Wednesday", "Saturday"]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const create = async () => {
    if (!ym) return setErr("Pick a month");
    if (days.length < 1 || days.length > 2) return setErr("Pick 1 or 2 days of the week");
    setErr("");
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/batches`,
        {
          course_id: courseId,
          name: batchName(ym, days),
          start_date: firstClassDate(ym, days),
          class_days: days,
          seats_total: Number(seats) || 10,
        },
        { headers: authHeaders() }
      );
      setOpen(false);
      setYm(presetYm || "");
      setSeats(10);
      setDays(["Wednesday", "Saturday"]);
      onCreated?.();
      onCancel?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not create batch");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        data-testid={`admin-add-batch-btn-${courseId}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase border border-dashed border-[rgba(28,22,16,0.4)] text-[#4a3e30] px-5 py-3 rounded-sm hover:border-[#DB0909] hover:text-[#DB0909] hover:bg-[#DB0909]/5 transition-all"
      >
        <Plus size={14} /> Add batch (new month)
      </button>
    );
  }
  const formTestId = presetYm
    ? `admin-add-batch-form-${courseId}-${presetYm}`
    : `admin-add-batch-form-${courseId}`;
  return (
    <div
      className={`border-2 border-[#DB0909]/60 bg-[#fefefc] rounded-sm shadow-sm ${compact ? "p-4 mt-4" : "p-5"} flex flex-wrap items-end gap-5`}
      data-testid={formTestId}
    >
      {!presetYm && (
        <div>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a3e30]">Month</p>
          <div className="mt-1.5">
            <MonthPicker courseId={courseId} value={ym} onChange={setYm} />
          </div>
        </div>
      )}
      {presetYm && (
        <div>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a3e30]">Month</p>
          <p className="mt-2 font-display font-black text-lg text-[#1c1610]" data-testid={`admin-add-preset-month-${courseId}-${presetYm}`}>
            {monthLabel(presetYm)}
          </p>
        </div>
      )}
      <div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a3e30]">Seats</p>
        <input
          type="number"
          min={1}
          value={seats}
          onChange={(e) => setSeats(e.target.value)}
          data-testid={`admin-add-seats-input-${courseId}`}
          className="mt-1 w-20 border border-[rgba(28,22,16,0.3)] px-3 py-2 font-mono text-sm bg-transparent focus:outline-none focus:border-[#DB0909] rounded-sm"
        />
      </div>
      <div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a3e30]">Class days <span className="text-[#4a3e30]/70">(1 or 2)</span></p>
        <div className="mt-1.5">
          <DaysPicker courseId={courseId} value={days} onChange={setDays} />
        </div>
      </div>
      {ym && days.length > 0 && (
        <MonthCalendarPreview ym={ym} days={days} />
      )}
      <div className="flex gap-2 ml-auto">
        <button data-testid={`admin-add-submit-${courseId}`} disabled={busy} onClick={create} className="font-mono text-[11px] tracking-[0.2em] uppercase bg-[#DB0909] text-[#f7f6f2] px-5 py-2.5 hover:bg-[#950606] transition-colors disabled:opacity-50 rounded-sm">
          {busy ? "Creating…" : "Create"}
        </button>
        <button onClick={() => { setOpen(false); onCancel?.(); }} className="font-mono text-[11px] tracking-[0.2em] uppercase border border-[rgba(28,22,16,0.3)] px-5 py-2.5 hover:border-[#DB0909] hover:text-[#DB0909] transition-colors rounded-sm">
          Cancel
        </button>
      </div>
      {err && <p className="w-full text-sm text-[#DB0909]" data-testid={`admin-add-error-${courseId}`}>{err}</p>}
    </div>
  );
};

const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  const suffix = y === 2026 ? "" : ` '${String(y).slice(2)}`;
  return `${MONTH_NAMES[m - 1]}${suffix}`;
};

const MonthCalendarPreview = ({ ym, days }) => {
  const [y, m] = ym.split("-").map(Number);
  const dayNums = new Set(days.map((d) => DAY_INDEX[d]));
  const first = new Date(y, m - 1, 1);
  const startWeekday = first.getDay(); // 0=Sun
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells = [];
  // We render Mon-Sun columns to feel natural
  const mondayFirstOffset = (startWeekday + 6) % 7; // convert Sun=0 -> 6, Mon=1 -> 0
  for (let i = 0; i < mondayFirstOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return (
    <div data-testid="admin-add-cal-preview">
      <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a3e30]">{monthLabel(ym)} preview</p>
      <div className="mt-1.5 inline-grid grid-cols-7 gap-[3px]" style={{ minWidth: 168 }}>
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <span key={i} className="font-mono text-[9px] tracking-[0.1em] uppercase text-[#4a3e30] w-6 text-center">
            {d}
          </span>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <span key={`e${i}`} className="w-6 h-6" />;
          const wd = new Date(y, m - 1, d).getDay();
          const on = dayNums.has(wd);
          return (
            <span
              key={`d${d}`}
              className={`w-6 h-6 flex items-center justify-center font-mono text-[10px] rounded-full ${
                on ? "bg-[#DB0909] text-[#f7f6f2] font-bold" : "text-[#4a3e30]"
              }`}
            >
              {d}
            </span>
          );
        })}
      </div>
    </div>
  );
};

const DaysEditor = ({ b, onSave }) => {
  const [days, setDays] = useState(b.class_days);
  useEffect(() => setDays(b.class_days), [b.class_days]);
  const dirty = JSON.stringify([...days].sort()) !== JSON.stringify([...b.class_days].sort());
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <DaysPicker courseId={b.id} value={days} onChange={setDays} />
      {dirty && days.length >= 1 && days.length <= 2 && (
        <button
          data-testid={`admin-days-save-${b.id}`}
          onClick={() => onSave(days)}
          className="font-mono text-[10px] tracking-[0.15em] uppercase bg-[#1c1610] text-[#f7f6f2] px-3 py-1.5 hover:bg-[#DB0909] transition-colors rounded-sm"
        >
          Save
        </button>
      )}
    </div>
  );
};

const initialsOf = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join("");

const BatchExtras = ({ batchId }) => {
  const [tab, setTab] = useState("students");
  const [roster, setRoster] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [annForm, setAnnForm] = useState({ title: "", body: "" });
  const [posting, setPosting] = useState(false);
  const [annErr, setAnnErr] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [r, m, a] = await Promise.all([
        axios.get(`${API}/api/admin/batches/${batchId}/roster`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/batches/${batchId}/materials`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/batches/${batchId}/announcements`, { headers: authHeaders() }),
      ]);
      setRoster(r.data);
      setMaterials(m.data);
      setAnnouncements(a.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [batchId]);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploadErr("");
    if (!/^(application\/pdf|image\/(png|jpe?g))$/.test(f.type)) { setUploadErr("Only PDF, PNG, JPEG allowed."); return; }
    if (f.size > 20 * 1024 * 1024) { setUploadErr("File too large — max 20MB."); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      await axios.post(`${API}/api/admin/batches/${batchId}/materials`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setUploadErr(typeof d === "string" ? d : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeMaterial = async (id) => {
    if (!window.confirm("Delete this material for the batch? Students will lose access immediately.")) return;
    await axios.delete(`${API}/api/admin/batches/${batchId}/materials/${id}`, { headers: authHeaders() });
    load();
  };

  const postAnnouncement = async (e) => {
    e.preventDefault();
    setAnnErr("");
    if (annForm.title.trim().length < 2 || annForm.body.trim().length < 1) { setAnnErr("Add a short title and message."); return; }
    setPosting(true);
    try {
      await axios.post(`${API}/api/admin/batches/${batchId}/announcements`, {
        title: annForm.title.trim(), body: annForm.body.trim(),
      }, { headers: authHeaders() });
      setAnnForm({ title: "", body: "" });
      await load();
    } catch (err) {
      const d = err.response?.data?.detail;
      setAnnErr(typeof d === "string" ? d : "Could not post — try again.");
    } finally {
      setPosting(false);
    }
  };

  const removeAnn = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await axios.delete(`${API}/api/admin/batches/${batchId}/announcements/${id}`, { headers: authHeaders() });
    load();
  };

  return (
    <details className="mt-4 border-t border-[rgba(28,22,16,0.1)] pt-3" data-testid={`admin-extras-${batchId}`}>
      <summary className="cursor-pointer font-mono text-[10px] tracking-[0.2em] uppercase text-[#950606] hover:text-[#DB0909] py-1">
        Roster & Content ({roster.length} students · {materials.length} files · {announcements.length} posts)
      </summary>
      <div className="mt-4 flex gap-1 border-b border-[rgba(28,22,16,0.14)] flex-wrap">
        {[
          ["students", <><Users size={12} /> Students</>],
          ["materials", <><FileText size={12} /> Materials</>],
          ["announcements", <><Bell size={12} /> Announcements</>],
        ].map(([id, label]) => (
          <button
            key={id}
            data-testid={`admin-extras-tab-${id}-${batchId}`}
            onClick={() => setTab(id)}
            className={`inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border-b-2 transition-colors ${
              tab === id ? "border-[#DB0909] text-[#1c1610]" : "border-transparent text-[#4a3e30] hover:text-[#1c1610]"
            }`}
          >{label}</button>
        ))}
      </div>

      {tab === "students" && (
        <div className="mt-4">
          {roster.length === 0 ? (
            <p className="text-xs text-[#4a3e30] font-light">No students in this batch yet.</p>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid={`admin-roster-list-${batchId}`}>
              {roster.map((row) => (
                <li
                  key={row.reservation_id}
                  data-testid={`admin-roster-row-${row.reservation_id}`}
                  className="flex items-start gap-3 border border-[rgba(28,22,16,0.12)] bg-[#fefefc] p-3"
                >
                  <span aria-hidden className="w-11 h-11 rounded-full overflow-hidden shrink-0 border border-[rgba(28,22,16,0.15)]">
                    {row.student?.profile_image ? (
                      <img src={row.student.profile_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#DB0909] to-[#950606] text-[#f7f6f2] font-display font-black text-sm">
                        {initialsOf(row.student?.name || row.name) || "?"}
                      </span>
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <p className="font-display font-bold text-sm text-[#1c1610] truncate">{row.student?.name || row.name}</p>
                      {row.student?.username && (
                        <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-[#950606] shrink-0">@{row.student.username}</span>
                      )}
                    </div>
                    <a href={`mailto:${row.email}`} className="block text-xs text-[#4a3e30] hover:text-[#DB0909] truncate">{row.email}</a>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <span className={`font-mono text-[9px] tracking-[0.15em] uppercase px-1.5 py-0.5 border ${
                        row.type === "dropin" ? "border-[#950606]/40 text-[#950606]" : "border-[#1c1610]/40 text-[#1c1610]"
                      }`}>
                        {row.type === "dropin" ? `${row.class_labels.length} drop-in` : "Full batch"}
                      </span>
                      {row.student ? (
                        <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-[#16a34a]">✓ Account linked</span>
                      ) : (
                        <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-[#4a3e30]/70">Guest booking</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "materials" && (
        <div className="mt-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              data-testid={`admin-material-upload-btn-${batchId}`}
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 bg-[#1c1610] text-[#f7f6f2] hover:bg-[#DB0909] transition-colors disabled:opacity-60"
            >
              <Upload size={12} /> {uploading ? "Uploading…" : "Upload PDF/PNG/JPEG"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,image/png,image/jpeg"
              onChange={handleFile}
              className="hidden"
              data-testid={`admin-material-file-${batchId}`}
            />
            <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#4a3e30]">Max 20MB · Visible to students in this batch only</span>
          </div>
          {uploadErr && <p data-testid={`admin-material-error-${batchId}`} className="mt-2 font-mono text-[10px] tracking-[0.15em] uppercase text-[#DB0909]">{uploadErr}</p>}

          {materials.length === 0 ? (
            <p className="mt-4 text-xs text-[#4a3e30] font-light">No materials uploaded yet.</p>
          ) : (
            <ul className="mt-4 space-y-2" data-testid={`admin-materials-list-${batchId}`}>
              {materials.map((m) => (
                <li key={m.id} className="flex items-center gap-3 border border-[rgba(28,22,16,0.12)] bg-[#f6efe3]/40 px-3 py-2.5">
                  <span className={`w-7 h-7 flex items-center justify-center shrink-0 ${m.kind === "pdf" ? "bg-[#DB0909]/15 text-[#DB0909]" : "bg-[#1c1610]/10 text-[#1c1610]"}`}>
                    {m.kind === "pdf" ? <FileText size={13} /> : <ImageIcon size={13} />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-sm text-[#1c1610] truncate">{m.filename}</p>
                    <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#4a3e30]">{(m.size / 1024).toFixed(0)}KB · {new Date(m.uploaded_at).toLocaleDateString("en-IN")}</p>
                  </div>
                  <button
                    data-testid={`admin-material-delete-${m.id}`}
                    onClick={() => removeMaterial(m.id)}
                    className="w-8 h-8 flex items-center justify-center border border-[rgba(28,22,16,0.3)] text-[#4a3e30] hover:border-[#DB0909] hover:text-[#DB0909] transition-colors"
                  ><Trash2 size={12} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "announcements" && (
        <div className="mt-4">
          <form onSubmit={postAnnouncement} className="space-y-2" data-testid={`admin-announcement-form-${batchId}`}>
            <input
              data-testid={`admin-announcement-title-${batchId}`}
              value={annForm.title}
              onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })}
              placeholder="Title (e.g., Class rescheduled to Sunday)"
              maxLength={120}
              className="w-full bg-transparent border border-[rgba(28,22,16,0.2)] px-3 py-2 text-sm text-[#1c1610] focus:outline-none focus:border-[#DB0909]"
            />
            <textarea
              data-testid={`admin-announcement-body-${batchId}`}
              value={annForm.body}
              onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })}
              placeholder="Say more — students in this batch will see it on their profile."
              maxLength={4000}
              rows={3}
              className="w-full bg-transparent border border-[rgba(28,22,16,0.2)] px-3 py-2 text-sm text-[#1c1610] focus:outline-none focus:border-[#DB0909] font-light"
            />
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={posting || annForm.title.trim().length < 2 || annForm.body.trim().length < 1}
                data-testid={`admin-announcement-post-${batchId}`}
                className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 bg-[#1c1610] text-[#f7f6f2] hover:bg-[#DB0909] transition-colors disabled:opacity-50"
              >
                <Bell size={12} /> {posting ? "Posting…" : "Post to batch"}
              </button>
              {annErr && <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#DB0909]">{annErr}</p>}
            </div>
          </form>

          {announcements.length === 0 ? (
            <p className="mt-4 text-xs text-[#4a3e30] font-light">No announcements yet.</p>
          ) : (
            <ul className="mt-4 space-y-3" data-testid={`admin-announcements-list-${batchId}`}>
              {announcements.map((a) => (
                <li key={a.id} className="border border-[rgba(28,22,16,0.12)] bg-[#f6efe3]/30 px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="font-display font-bold text-sm text-[#1c1610]">{a.title}</p>
                    <button
                      data-testid={`admin-announcement-delete-${a.id}`}
                      onClick={() => removeAnn(a.id)}
                      className="text-[10px] text-[#4a3e30] hover:text-[#DB0909] font-mono tracking-[0.15em] uppercase"
                    >Delete</button>
                  </div>
                  <p className="mt-1 text-xs text-[#4a3e30] font-light leading-relaxed whitespace-pre-wrap">{a.body}</p>
                  <p className="mt-1 font-mono text-[9px] tracking-[0.2em] uppercase text-[#4a3e30]/70">{new Date(a.created_at).toLocaleString("en-IN")}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </details>
  );
};


const BatchCard = ({ b, patchBatch, deleteBatch, onCreated }) => {
  const [addingSame, setAddingSame] = useState(false);
  const ym = b.start_date.slice(0, 7);
  return (
  <div data-testid={`admin-batch-${b.course_id}`} className={`border bg-[#fefefc] p-5 rounded-sm shadow-[0_1px_0_rgba(28,22,16,0.03)] transition-shadow hover:shadow-[0_4px_12px_rgba(28,22,16,0.06)] ${b.visible === false ? "border-dashed border-[rgba(28,22,16,0.35)] opacity-70" : "border-[rgba(28,22,16,0.15)]"}`}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p data-testid={`admin-batch-name-${b.id}`} className="font-display font-bold text-xl text-[#1c1610]">{b.name}</p>
        <p className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a3e30] flex flex-wrap items-center gap-2">
          <span className={`px-2 py-0.5 rounded-sm ${b.status === "in_progress" ? "bg-[#DB0909]/10 text-[#DB0909]" : "bg-[rgba(28,22,16,0.06)]"}`}>{b.status.replace("_", " ")}</span>
          <span>· starts {b.start_date}</span>
          <span>· {b.reservations_count} reservations</span>
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          data-testid={`admin-batch-add-same-${b.id}`}
          onClick={() => setAddingSame((v) => !v)}
          className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-2 border border-dashed border-[#DB0909]/40 text-[#950606] hover:bg-[#DB0909]/10 hover:border-[#DB0909] transition-colors rounded-sm"
        >
          <Plus size={13} /> Add batch to this month
        </button>
        {b.seats_taken === 0 && (
          <button
            data-testid={`admin-batch-delete-${b.id}`}
            onClick={() => deleteBatch(b)}
            className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-2 border border-[rgba(28,22,16,0.3)] text-[#4a3e30] hover:border-[#DB0909] hover:text-[#DB0909] transition-colors rounded-sm"
          >
            <Trash2 size={13} /> Delete
          </button>
        )}
        <button
          data-testid={`admin-visible-toggle-${b.id}`}
          onClick={() => patchBatch(b.id, { visible: !(b.visible !== false) })}
          className={`inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] uppercase px-4 py-2 border transition-colors rounded-sm ${
            b.visible !== false ? "border-[#DB0909] text-[#950606] hover:bg-[#DB0909] hover:text-[#f7f6f2]" : "border-[rgba(28,22,16,0.35)] text-[#4a3e30] hover:border-[#DB0909] hover:text-[#DB0909]"
          }`}
        >
          {b.visible !== false ? <><Eye size={13} /> Shown on site</> : <><EyeOff size={13} /> Hidden</>}
        </button>
      </div>
    </div>

    <div className="mt-5 flex flex-wrap gap-x-10 gap-y-4 items-start">
      <div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a3e30]">Seats filled</p>
        <p className="mt-1 font-mono font-bold text-lg text-[#1c1610]" data-testid={`admin-seats-${b.course_id}`}>
          {b.seats_taken}/{b.seats_total} <span className="text-[#DB0909] text-sm">({b.spots_left} left)</span>
        </p>
      </div>
      <div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a3e30]">Batch strength</p>
        <div className="mt-1"><StrengthEditor b={b} onSave={(v) => patchBatch(b.id, { seats_total: v })} /></div>
      </div>
      <div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a3e30]">Classes completed</p>
        <div className="flex items-center gap-3 mt-1">
          <button data-testid={`admin-class-minus-${b.course_id}`} onClick={() => patchBatch(b.id, { classes_completed: b.classes_completed - 1 })} className="w-8 h-8 border border-[rgba(28,22,16,0.3)] hover:border-[#DB0909] hover:text-[#DB0909] rounded-sm">−</button>
          <span className="font-mono font-bold text-lg" data-testid={`admin-class-count-${b.course_id}`}>{b.classes_completed}/{b.total_classes}</span>
          <button data-testid={`admin-class-plus-${b.course_id}`} onClick={() => patchBatch(b.id, { classes_completed: b.classes_completed + 1 })} className="w-8 h-8 border border-[rgba(28,22,16,0.3)] hover:border-[#DB0909] hover:text-[#DB0909] rounded-sm">+</button>
        </div>
      </div>
      <div>
        <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#4a3e30]">Class days <span className="text-[#4a3e30]/70">(1 or 2)</span></p>
        <div className="mt-1"><DaysEditor b={b} onSave={(days) => patchBatch(b.id, { class_days: days })} /></div>
      </div>
    </div>

    {addingSame && (
      <AddBatchForm
        courseId={b.course_id}
        presetYm={ym}
        compact
        onCreated={onCreated}
        onCancel={() => setAddingSame(false)}
      />
    )}

    {b.course_id === "indie" && (
      <details className="mt-4 border-t border-[rgba(28,22,16,0.1)] pt-3" data-testid={`admin-checklist-${b.id}`} open={b.status === "in_progress"}>
        <summary className="cursor-pointer font-mono text-[10px] tracking-[0.2em] uppercase text-[#950606] hover:text-[#DB0909] py-1">
          Track list — tick classes as they're done ({b.classes_completed}/{b.total_classes})
        </summary>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
          {b.sessions.map((s, i) => {
            const done = i < b.classes_completed;
            return (
              <label key={i} data-testid={`admin-class-check-${b.id}-${i}`} className="flex items-center gap-2.5 cursor-pointer text-sm group py-1">
                <input type="checkbox" checked={done} onChange={() => patchBatch(b.id, { classes_completed: done ? i : i + 1 })} className="w-4 h-4 accent-[#DB0909]" />
                <span className={`font-mono text-[10px] ${done ? "text-[#950606]" : "text-[#4a3e30]"}`}>{String(i + 1).padStart(2, "0")}</span>
                <span className={done ? "line-through text-[#4a3e30]" : "text-[#1c1610] group-hover:text-[#950606]"}>{s}</span>
              </label>
            );
          })}
        </div>
      </details>
    )}

    <BatchExtras batchId={b.id} />
  </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("abc_admin_token"));
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [batches, setBatches] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [tab, setTab] = useState("batches");

  const load = async () => {
    try {
      const [b, r] = await Promise.all([
        axios.get(`${API}/api/admin/batches`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/reservations`, { headers: authHeaders() }),
      ]);
      setBatches(b.data);
      setReservations(r.data);
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  };

  useEffect(() => {
    if (token) load();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async () => {
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, form);
      localStorage.setItem("abc_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("abc_admin_token");
    setToken(null);
  };

  const patchBatch = async (id, body) => {
    await axios.patch(`${API}/api/admin/batches/${id}`, body, { headers: authHeaders() });
    load();
  };

  const deleteReservation = async (id, name) => {
    if (!window.confirm(`Delete reservation for ${name}? This frees up one seat in the batch.`)) return;
    await axios.delete(`${API}/api/admin/reservations/${id}`, { headers: authHeaders() });
    load();
  };

  const moveReservation = async (id, targetBatchId) => {
    try {
      await axios.patch(`${API}/api/admin/reservations/${id}/batch`, { batch_id: targetBatchId }, { headers: authHeaders() });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert(typeof d === "string" ? d : "Could not move reservation. Check batch capacity.");
    }
  };

  const deleteBatch = async (b) => {
    if (!window.confirm(`Delete "${b.name}" (${COURSE_LABELS[b.course_id]})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/batches/${b.id}`, { headers: authHeaders() });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      window.alert(typeof d === "string" ? d : "Could not delete batch");
    }
  };

  if (!token) {
    return (
      <div className="admin-page min-h-screen bg-[#f7f6f2] flex items-center justify-center px-6">
        <div className="w-full max-w-sm border border-[rgba(28,22,16,0.2)] bg-[#fefefc] p-8">
          <h1 className="font-display font-black text-2xl text-[#1c1610]">Admin — abc guitar lessons</h1>
          <input data-testid="admin-email-input" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-6 w-full border border-[rgba(28,22,16,0.2)] px-4 py-3 text-sm bg-transparent focus:outline-none focus:border-[#DB0909]" />
          <input data-testid="admin-password-input" placeholder="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} onKeyDown={(e) => e.key === "Enter" && login()} className="mt-3 w-full border border-[rgba(28,22,16,0.2)] px-4 py-3 text-sm bg-transparent focus:outline-none focus:border-[#DB0909]" />
          {error && <p data-testid="admin-login-error" className="mt-3 text-sm text-[#DB0909]">{error}</p>}
          <button data-testid="admin-login-btn" onClick={login} className="mt-5 w-full font-mono text-xs tracking-[0.25em] uppercase bg-[#1c1610] text-[#f7f6f2] py-3.5 hover:bg-[#DB0909] transition-colors">
            Sign in
          </button>
        </div>
      </div>
    );
  }

  const active = batches.filter((b) => b.classes_completed < b.total_classes);
  const totalSeatsTaken = active.reduce((s, b) => s + b.seats_taken, 0);
  const inProgress = active.filter((b) => b.status === "in_progress").length;

  return (
    <div className="admin-page min-h-screen bg-[#f7f6f2] px-4 sm:px-6 md:px-12 py-8" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/" data-testid="admin-home-link" className="flex items-center gap-3 group" title="Back to site">
            <Logo className="h-10 md:h-12" />
            <span className="hidden sm:inline font-mono text-[10px] tracking-[0.25em] uppercase text-[#4a3e30] group-hover:text-[#DB0909] transition-colors">
              abc guitar lessons
            </span>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#DB0909] border border-[#DB0909]/40 px-2 py-1">
              Admin
            </span>
          </Link>
          <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase border border-[rgba(28,22,16,0.3)] px-4 py-2.5 hover:border-[#DB0909] hover:text-[#DB0909] transition-colors">
            <LogOut size={12} /> Log out
          </button>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3" data-testid="admin-stats">
          {[
            ["Upcoming & live batches", active.length],
            ["Batches in progress", inProgress],
            ["Total reservations", reservations.length],
          ].map(([label, val]) => (
            <div key={label} className="border border-[rgba(28,22,16,0.15)] bg-[#fefefc] p-4">
              <p className="font-display font-black text-2xl md:text-3xl text-[#950606]">{val}</p>
              <p className="mt-1 font-mono text-[9px] md:text-[10px] tracking-[0.15em] uppercase text-[#4a3e30]">{label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex border-b border-[rgba(28,22,16,0.15)]">
          {[["batches", "Batches"], ["reservations", `Reservations (${reservations.length})`]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`font-mono text-[11px] tracking-[0.2em] uppercase px-6 py-3.5 border-b-2 -mb-px transition-colors ${
                tab === id ? "border-[#DB0909] text-[#950606]" : "border-transparent text-[#4a3e30] hover:text-[#1c1610]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "batches" && (
          <div className="mt-8 space-y-12">
            {["indie", "songs"].map((cid) => {
              const courseBatches = batches.filter((b) => b.course_id === cid);
              return (
                <section key={cid} data-testid={`admin-course-section-${cid}`}>
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <h2 className="inline-flex items-center gap-3 font-display font-black text-xl md:text-2xl text-[#1c1610]">
                      <Music size={18} className="text-[#DB0909]" /> {COURSE_LABELS[cid]}
                    </h2>
                    {courseBatches.length === 0 && (
                      <AddBatchForm courseId={cid} onCreated={load} />
                    )}
                  </div>
                  <div className="mt-4 grid gap-4">
                    {courseBatches.map((b) => (
                      <BatchCard key={b.id} b={b} patchBatch={patchBatch} deleteBatch={deleteBatch} onCreated={load} />
                    ))}
                    {courseBatches.length === 0 && (
                      <p className="text-sm text-[#4a3e30] flex items-center gap-2"><CalendarPlus size={14} /> No batches yet — add your first one above.</p>
                    )}
                    {courseBatches.length > 0 && (
                      <AddBatchForm courseId={cid} onCreated={load} />
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        )}

        {tab === "reservations" && (
          <div className="mt-8 border border-[rgba(28,22,16,0.2)] bg-[#fefefc] overflow-x-auto" data-testid="admin-reservations">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] tracking-[0.15em] uppercase text-[#4a3e30] border-b border-[rgba(28,22,16,0.15)]">
                  <th className="p-3">Name</th><th className="p-3">Contact</th><th className="p-3">Course / Batch</th><th className="p-3">Type</th><th className="p-3">Amount</th><th className="p-3">When</th><th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {reservations.length === 0 && (
                  <tr><td colSpan="7" className="p-6 text-center text-[#4a3e30]">No reservations yet</td></tr>
                )}
                {reservations.map((r) => {
                  const targets = batches.filter((b) => b.id !== r.batch_id && b.status !== "completed" && (b.seats_total - b.seats_taken) > 0);
                  const byCourse = targets.reduce((acc, b) => { (acc[b.course_id] ||= []).push(b); return acc; }, {});
                  return (
                    <tr key={r.id} className="border-b border-[rgba(28,22,16,0.08)]">
                      <td className="p-3 font-medium text-[#1c1610]">{r.name}</td>
                      <td className="p-3 text-[#4a3e30]">{r.phone}<br />{r.email}</td>
                      <td className="p-3 text-[#4a3e30]">{r.course_name}<br />{r.batch_name}{r.type === "dropin" && r.class_labels?.length > 0 && <span className="block text-xs">({r.class_labels.join(", ")})</span>}</td>
                      <td className="p-3 font-mono text-xs uppercase">{r.type}</td>
                      <td className="p-3 font-mono text-[#950606]">{r.currency === "USD" ? "$" : "₹"}{r.amount?.toLocaleString("en-IN")}</td>
                      <td className="p-3 text-xs text-[#4a3e30]">{new Date(r.created_at).toLocaleString("en-IN")}</td>
                      <td className="p-3">
                        <div className="flex flex-col gap-1.5 items-start">
                          {targets.length > 0 && (
                            <select
                              data-testid={`admin-reservation-move-${r.id}`}
                              defaultValue=""
                              onChange={(e) => {
                                const v = e.target.value;
                                e.target.value = "";
                                if (!v) return;
                                const target = batches.find((b) => b.id === v);
                                const crossCourse = target && target.course_id !== r.course_id;
                                const msg = crossCourse
                                  ? `Move ${r.name} from ${r.course_name} to ${COURSE_LABELS[target.course_id]} — ${target.name}?\n\nNote: drop-in class picks will be cleared. Paid amount stays as-is.`
                                  : `Move ${r.name} to ${target?.name}?`;
                                if (window.confirm(msg)) moveReservation(r.id, v);
                              }}
                              className="font-mono text-[10px] tracking-[0.1em] uppercase border border-[rgba(28,22,16,0.3)] text-[#4a3e30] px-2 py-1.5 bg-transparent hover:border-[#DB0909] transition-colors"
                            >
                              <option value="" disabled>Move to…</option>
                              {Object.entries(byCourse).map(([cid, list]) => (
                                <optgroup key={cid} label={`${COURSE_LABELS[cid] || cid}${cid !== r.course_id ? " (other course)" : ""}`}>
                                  {list.map((b) => (
                                    <option key={b.id} value={b.id}>{b.name} ({b.seats_total - b.seats_taken} left)</option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                          )}
                          <button
                            data-testid={`admin-reservation-delete-${r.id}`}
                            onClick={() => deleteReservation(r.id, r.name)}
                            className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] uppercase border border-[rgba(28,22,16,0.3)] text-[#4a3e30] px-3 py-2 hover:border-[#DB0909] hover:text-[#DB0909] transition-colors"
                          >
                            <Trash2 size={11} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
